import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Trophy,
  Plus,
  Search,
  TrendingUp,
  ArrowUpRight,
  ChevronRight,
  Star,
  Tag,
  Calendar,
  Target,
  Sparkles,
  Filter,
  RefreshCw,
} from 'lucide-react';
import { successCasesApi } from '@/api/successCases';
import type { SuccessCaseListItem, SuccessCaseSummary, SuccessCaseCreate } from '@/types/data';
import { useAuthStore } from '@/stores/authStore';

const CATEGORIES = [
  { value: 'conversion', label: 'コンバージョン' },
  { value: 'engagement', label: 'エンゲージメント' },
  { value: 'retention', label: 'リテンション' },
  { value: 'acquisition', label: '獲得' },
];

const METRIC_TYPES = [
  { value: 'conversion_rate', label: 'CVR' },
  { value: 'daily_registrations', label: '日次登録数' },
  { value: 'click_rate', label: 'クリック率' },
  { value: 'retention_rate', label: '継続率' },
  { value: 'revenue', label: '売上' },
  { value: 'other', label: 'その他' },
];

export default function SuccessCases() {
  const queryClient = useQueryClient();
  const { accessibleCompanies } = useAuthStore();

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'summary' | 'list' | 'detect'>('summary');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [metricTypeFilter, setMetricTypeFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // 初期法人選択
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // サマリー取得
  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['success-cases-summary', selectedCompanyId],
    queryFn: () => successCasesApi.getSummary({ company_id: selectedCompanyId! }),
    enabled: !!selectedCompanyId,
  });

  // 成功事例一覧取得
  const { data: cases, isLoading: casesLoading } = useQuery({
    queryKey: ['success-cases', selectedCompanyId, categoryFilter, metricTypeFilter],
    queryFn: () =>
      successCasesApi.list({
        company_id: selectedCompanyId!,
        category: categoryFilter || undefined,
        metric_type: metricTypeFilter || undefined,
        include_public: true,
      }),
    enabled: !!selectedCompanyId,
  });

  // 自動検出
  const detectMutation = useMutation({
    mutationFn: (params: { company_id: number; period_days?: number; min_improvement_percent?: number }) =>
      successCasesApi.autoDetect(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['success-cases'] });
      queryClient.invalidateQueries({ queryKey: ['success-cases-summary'] });
    },
  });

  // 成功事例作成
  const createMutation = useMutation({
    mutationFn: (data: SuccessCaseCreate) => successCasesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['success-cases'] });
      queryClient.invalidateQueries({ queryKey: ['success-cases-summary'] });
    },
  });

  const handleDetect = useCallback(() => {
    if (!selectedCompanyId) return;
    detectMutation.mutate({
      company_id: selectedCompanyId,
      period_days: 30,
      min_improvement_percent: 20,
    });
  }, [selectedCompanyId, detectMutation]);

  const handleSaveDetected = useCallback(
    async (detected: SuccessCaseCreate) => {
      await createMutation.mutateAsync(detected);
    },
    [createMutation]
  );

  const filteredCases = (Array.isArray(cases) ? cases : []).filter((c) =>
    searchQuery ? c.title.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  const getCategoryLabel = (value: string) =>
    CATEGORIES.find((c) => c.value === value)?.label || value;

  const getMetricTypeLabel = (value: string) =>
    METRIC_TYPES.find((m) => m.value === value)?.label || value;

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const renderSummaryTab = (summaryData: SuccessCaseSummary) => (
    <div className="space-y-6">
      {/* 概要カード */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">総事例数</p>
              <p className="text-2xl font-bold text-gray-900">{summaryData.total_cases}</p>
            </div>
            <Trophy className="w-10 h-10 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">平均改善率</p>
              <p className="text-2xl font-bold text-green-600">
                {summaryData.avg_improvement_rate
                  ? `+${summaryData.avg_improvement_rate.toFixed(1)}%`
                  : '-'}
              </p>
            </div>
            <TrendingUp className="w-10 h-10 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">カテゴリ数</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.keys(summaryData.by_category ?? {}).length}
              </p>
            </div>
            <Filter className="w-10 h-10 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">指標タイプ数</p>
              <p className="text-2xl font-bold text-gray-900">
                {Object.keys(summaryData.by_metric_type ?? {}).length}
              </p>
            </div>
            <Target className="w-10 h-10 text-purple-500" />
          </div>
        </div>
      </div>

      {/* カテゴリ別・指標別 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">カテゴリ別事例数</h3>
          <div className="space-y-3">
            {Object.entries(summaryData.by_category ?? {}).map(([category, count]) => (
              <div key={category} className="flex items-center justify-between">
                <span className="text-gray-700">{getCategoryLabel(category)}</span>
                <span className="font-medium text-gray-900">{count}件</span>
              </div>
            ))}
            {Object.keys(summaryData.by_category ?? {}).length === 0 && (
              <p className="text-gray-500 text-sm">データがありません</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">指標タイプ別事例数</h3>
          <div className="space-y-3">
            {Object.entries(summaryData.by_metric_type ?? {}).map(([metricType, count]) => (
              <div key={metricType} className="flex items-center justify-between">
                <span className="text-gray-700">{getMetricTypeLabel(metricType)}</span>
                <span className="font-medium text-gray-900">{count}件</span>
              </div>
            ))}
            {Object.keys(summaryData.by_metric_type ?? {}).length === 0 && (
              <p className="text-gray-500 text-sm">データがありません</p>
            )}
          </div>
        </div>
      </div>

      {/* トップ事例 */}
      {(summaryData.top_cases?.length ?? 0) > 0 && (
        <div className="bg-white rounded-lg shadow p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500" />
            改善率トップ事例
          </h3>
          <div className="space-y-4">
            {summaryData.top_cases.map((c, index) => (
              <div
                key={c.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <span className="w-8 h-8 rounded-full bg-yellow-100 text-yellow-700 flex items-center justify-center font-bold">
                    {index + 1}
                  </span>
                  <div>
                    <p className="font-medium text-gray-900">{c.title}</p>
                    <p className="text-sm text-gray-500">
                      {getCategoryLabel(c.category)} / {getMetricTypeLabel(c.metric_type)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-600">
                    {c.improvement_rate ? `+${c.improvement_rate.toFixed(1)}%` : '-'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formatDate(c.period_start)} ~ {formatDate(c.period_end)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderListTab = (casesList: SuccessCaseListItem[]) => (
    <div className="space-y-4">
      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="タイトルで検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">全カテゴリ</option>
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <select
            value={metricTypeFilter}
            onChange={(e) => setMetricTypeFilter(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="">全指標</option>
            {METRIC_TYPES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 事例一覧 */}
      {casesList.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">成功事例がありません</p>
          <p className="text-sm text-gray-400 mt-2">
            「自動検出」タブで成功事例を検出できます
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {casesList.map((c) => (
            <div
              key={c.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-5"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 line-clamp-2">{c.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                      {getCategoryLabel(c.category)}
                    </span>
                    {c.is_public && (
                      <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                        公開
                      </span>
                    )}
                  </div>
                </div>
                {c.improvement_rate && (
                  <div className="flex items-center gap-1 text-green-600 font-bold">
                    <ArrowUpRight className="w-4 h-4" />
                    {c.improvement_rate.toFixed(1)}%
                  </div>
                )}
              </div>

              <div className="space-y-2 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  <span>
                    {getMetricTypeLabel(c.metric_type)}: {c.metric_value.toLocaleString()}
                    {c.metric_unit || ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {formatDate(c.period_start)} ~ {formatDate(c.period_end)}
                  </span>
                </div>
              </div>

              {c.tags.length > 0 && (
                <div className="flex items-center gap-1 mt-3 flex-wrap">
                  <Tag className="w-3 h-3 text-gray-400" />
                  {c.tags.slice(0, 3).map((tag) => (
                    <span
                      key={tag}
                      className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {c.tags.length > 3 && (
                    <span className="text-xs text-gray-400">+{c.tags.length - 3}</span>
                  )}
                </div>
              )}

              <button className="mt-4 w-full text-sm text-primary hover:text-primary/80 flex items-center justify-center gap-1">
                詳細を見る
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderDetectTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="w-6 h-6 text-purple-500" />
          <h3 className="text-lg font-semibold text-gray-900">成功事例の自動検出</h3>
        </div>
        <p className="text-gray-600 mb-6">
          過去30日間のデータを分析し、20%以上の改善が見られた指標を成功事例として自動検出します。
        </p>
        <button
          onClick={handleDetect}
          disabled={detectMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {detectMutation.isPending ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin" />
              検出中...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              自動検出を実行
            </>
          )}
        </button>
      </div>

      {/* 検出結果 */}
      {detectMutation.data && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            検出結果 ({detectMutation.data.length}件)
          </h3>
          {detectMutation.data.length === 0 ? (
            <p className="text-gray-500">
              条件を満たす成功事例は検出されませんでした。
            </p>
          ) : (
            <div className="space-y-4">
              {detectMutation.data.map((detected, index) => (
                <div
                  key={index}
                  className="p-4 border rounded-lg hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-gray-900">{detected.title}</h4>
                      <p className="text-sm text-gray-500 mt-1">{detected.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                          {getCategoryLabel(detected.category)}
                        </span>
                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                          {getMetricTypeLabel(detected.metric_type)}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      {detected.improvement_rate && (
                        <p className="text-lg font-bold text-green-600">
                          +{detected.improvement_rate.toFixed(1)}%
                        </p>
                      )}
                      <button
                        onClick={() => handleSaveDetected(detected)}
                        disabled={createMutation.isPending}
                        className="mt-2 text-sm px-3 py-1 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
                      >
                        <Plus className="w-3 h-3 inline mr-1" />
                        保存
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* ヘッダー */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Trophy className="w-7 h-7 text-yellow-500" />
              成功事例ライブラリ
            </h1>
            <p className="text-gray-500 mt-1">
              高パフォーマンスの施策や改善事例を記録・共有
            </p>
          </div>

          {accessibleCompanies.length > 1 && (
            <select
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
              className="border rounded-lg px-3 py-2"
            >
              <option value="">法人を選択</option>
              {accessibleCompanies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.display_name || c.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedCompanyId ? (
          <>
            {/* タブ */}
            <div className="flex gap-2 mb-6 border-b">
              <button
                onClick={() => setActiveTab('summary')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'summary'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                サマリー
              </button>
              <button
                onClick={() => setActiveTab('list')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'list'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                事例一覧
              </button>
              <button
                onClick={() => setActiveTab('detect')}
                className={`px-4 py-2 font-medium transition-colors ${
                  activeTab === 'detect'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                自動検出
              </button>
            </div>

            {/* コンテンツ */}
            {summaryLoading || casesLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                {activeTab === 'summary' && summary && renderSummaryTab(summary)}
                {activeTab === 'list' && filteredCases && renderListTab(filteredCases)}
                {activeTab === 'detect' && renderDetectTab()}
              </>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">法人を選択してください</p>
          </div>
        )}
      </div>
    </div>
  );
}
